/* ------------------------ Functional imports start ------------------------ */
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import LogTool from '../../../logger/logTools'
import { type FileCollection, type FileInfo } from '../../../utils/types'
// React Query imports
import { useDeleteFileFromUrl } from '../hooks/useFilesystemMutations'
import { fetchFileList } from '../api/fileApi'
import { useUserContext } from '../../../utils/context'
import { useWebsocket } from '../../../utils/WebsocketProvider'
import useMobileService from '../../../hooks/useMobileService'
import { isPrivileged } from '../../../utils/functions'
import { type Article } from '../../Item/utils/types'
import { fetchBOMLeafArticles, fetchBOMRoot, type BOMLeafArticle } from '../../Bom/api/bomApi'
/* ------------------------- Functional imports end ------------------------- */
/* -------------------------- Design imports start -------------------------- */
import { toast } from 'react-toastify'
import MultilevelDrawer from '../../../components/layout/MultilevelDrawer'
import {
  Box,
  CircularProgress,
  Grid,
  Typography,
} from '@mui/material'
import UploadedFilesTable from './UploadedFilesTable'
import DeleteModal from '../../../components/widgets/DeleteModal'
/* --------------------------- Design imports end --------------------------- */

type Props = {
  open: boolean
  setOpen: (open: boolean) => void
  onClose?: () => void
  onConfirm?: () => void
  // the FilesDrawer can be used with a simple list of files or with multiple file collections
  files?: FileInfo[] | FileCollection[] | string
  title?: string
  size?: 'big' | 'small'
  // Optional refresh trigger that forces a reload when changed
  refreshKey?: number | string
  accessSecret?: string
  downloadOnly?: boolean
}

const ensureTrailingSlash = (url: string): string =>
  url.endsWith('/') ? url : `${url}/`

const stripQueryString = (url: string): string => url.split('?')[0]

const deriveArticleSelfFromFilesUrl = (url: string): string | undefined => {
  const cleaned = ensureTrailingSlash(stripQueryString(url))
  const match = cleaned.match(/(.*\/items\/articles\/\d+\/)files\/$/)
  return match?.[1]
}

const extractSelfUrl = (value: unknown): string | undefined => {
  if (typeof value === 'string' && value) return value
  if (typeof value === 'object' && value !== null && 'self' in value) {
    const self = (value as { self?: unknown }).self
    if (typeof self === 'string' && self) return self
  }
  return undefined
}

const deriveListUrlFromFile = (file?: FileInfo): string | undefined => {
  if (!file) return undefined
  if (file.uploadUrl) {
    return ensureTrailingSlash(file.uploadUrl)
  }
  const parentUrl =
    extractSelfUrl((file as any).request) ??
    extractSelfUrl((file as any).guestRequest ?? (file as any).guest_request) ??
    extractSelfUrl((file as any).article) ??
    extractSelfUrl((file as any).contract) ??
    extractSelfUrl((file as any).offer) ??
    extractSelfUrl((file as any).bill)
  if (parentUrl) {
    return ensureTrailingSlash(`${ensureTrailingSlash(parentUrl)}files`)
  }
  if (file.self) {
    const trimmed = file.self.endsWith('/') ? file.self.slice(0, -1) : file.self
    const lastSlash = trimmed.lastIndexOf('/')
    if (lastSlash !== -1) {
      return ensureTrailingSlash(trimmed.slice(0, lastSlash + 1))
    }
  }
  return undefined
}

const resolveCollectionListUrl = (collection: FileCollection): string | undefined => {
  if (collection.filesUrl) return ensureTrailingSlash(collection.filesUrl)
  if (collection.files.length > 0) {
    return deriveListUrlFromFile(collection.files[0])
  }
  return undefined
}

const buildLeafArticleTitle = (leaf: BOMLeafArticle): string => {
  const baseTitle = leaf.name ? `${leaf.number} ${leaf.name}` : leaf.number ?? 'Article'
  if (typeof leaf.bom_amount === 'number' && leaf.bom_amount !== 1) {
    return `${baseTitle} x${leaf.bom_amount}`
  }
  return baseTitle
}

const isFileCollectionArray = (value: unknown[]): value is FileCollection[] =>
  value.length > 0 && typeof value[0] === 'object' && value[0] !== null && 'files' in value[0]

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
function FilesDrawer(props: Props) {
  /* -------------------------- Non state data start -------------------------- */
  const {
    open,
    setOpen,
    onClose = () => null,
    onConfirm = () => null,
    files,
    title,
    size = 'big',
    refreshKey,
    accessSecret,
    downloadOnly = false,
  } = props
  const { t } = useTranslation()
  const log = new LogTool({ context: 'FilesDrawer', enable: true, logLevel: 'debug' })
  const { user } = useUserContext()
  const { websocketMessage } = useWebsocket()
  const isMobile = useMobileService()

  /* --------------------------- Non state data end --------------------------- */

  /* ------------------------- React Query Hooks ------------------------- */
  const deleteFileMutation = useDeleteFileFromUrl()
  /* --------------------------------------------------------------------- */

  /* ---------------------------- Flag states start --------------------------- */
  const [loading, setLoading] = useState(false)
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [localInfoPanelOpen, setLocalInfoPanelOpen] = useState(false)
  const [socketRefreshKey, setSocketRefreshKey] = useState(0)
  /* ----------------------------- Flag states end ---------------------------- */

  /* ---------------------------- Data states start --------------------------- */
  const [fileCollections, setFileCollections] = useState<FileCollection[] | null>(null)
  const [fileList, setFileList] = useState<FileInfo[] | null>(null)
  const [filesToDelete, setFilesToDelete] = useState<FileInfo[]>([])
  const [deleteFileName, setDeleteFileName] = useState<string>('')
  const fileListUrl = typeof files === 'string'
    ? ensureTrailingSlash(files)
    : deriveListUrlFromFile(
        fileList?.[0]
          ?? (Array.isArray(files) && !isFileCollectionArray(files)
            ? files[0]
            : undefined)
      )
  /* ----------------------------- Data states end ---------------------------- */

  /* ------------------------------ Effects start ----------------------------- */
  useEffect(() => {
    // Only update state if files is an array (not a string URL)
    // String URLs should be handled by the refreshFiles effect below
    if(!Array.isArray(files)) {
      // If files is a string URL, clear the fileList state to force a fetch
      if(typeof files === 'string') {
        // Clear fileList so refreshFiles will fetch using the string URL
        if(fileList !== null) setFileList(null)
        if(fileCollections !== null) setFileCollections(null)
      }
      // If files is undefined, also clear state
      if(files === undefined) {
        if(fileList !== null) setFileList(null)
        if(fileCollections !== null) setFileCollections(null)
      }
      return
    }
    // whenever the files input changes, we should update the render state: fileCollections or fileList
    // This is done in an effect rather in the pre-render section, as an effect makes it easier
    // to react on changes of the files input.
    
    // Determine if files is FileInfo[] or FileCollection[]
    const isFileCollectionArray = files.length > 0 && typeof files[0] === 'object' && files[0] !== null && 'files' in files[0]
    
    if(isFileCollectionArray) {
      // files is FileCollection[]
      setFileCollections(files as FileCollection[])
      setFileList(null) // Clear fileList when using collections
    } else {
      // files is FileInfo[]
      setFileList(files as FileInfo[])
      setFileCollections(null) // Clear fileCollections when using fileList
    }
  }, [files])

  useEffect(() => {
    console.log('fileCollections', fileCollections)
  }, [fileCollections])

  useEffect(() => {
    if (!open) return

    let isCancelled = false

    // Use current files prop value directly - it's in dependencies so it's current
    const currentFiles = files
    
    const refreshFiles = async (): Promise<void> => {
      try {
        if (Array.isArray(currentFiles) && isFileCollectionArray(currentFiles)) {
          const collections = currentFiles
          
          // Check if any collection has files already - if so, use them immediately and refresh in background
          const hasInitialFiles = collections.some(collection => collection.files && collection.files.length > 0)
          
          // If collections already have files, set them immediately without loading state
          // Check if current state is different or empty to avoid unnecessary updates
          const needsInitialSet = hasInitialFiles && (
            fileCollections === null || 
            fileCollections.length === 0 ||
            fileCollections.length !== collections.length ||
            !fileCollections.some((fc, idx) => fc.files?.length === collections[idx]?.files?.length)
          )
          
          if (needsInitialSet) {
            setFileCollections(collections)
          } else if (!hasInitialFiles) {
            // Only set loading if we don't have initial files
            setLoading(true)
          }
          
          const refreshedCollections = await Promise.allSettled(
            collections.map(async collection => {
              const listUrl = resolveCollectionListUrl(collection)
              if (!listUrl) return [collection]

              // Preserve article information from original files when refreshing
              const originalFiles = collection.files || []
              const articleInfoMap = new Map<string, Article | string>()
              originalFiles.forEach((file: FileInfo) => {
                if (file.self && file.article) {
                  articleInfoMap.set(file.self, file.article)
                }
              })

              const response = await fetchFileList(listUrl, { accessSecret })
              // Restore article information to refreshed files
              let filesWithArticleInfo = response.results.map((file: FileInfo) => {
                if (file.self && articleInfoMap.has(file.self)) {
                  return {
                    ...file,
                    article: articleInfoMap.get(file.self),
                  } as FileInfo
                }
                return file
              })

              if (filesWithArticleInfo.length === 0) {
                const articleSelf: string | undefined = deriveArticleSelfFromFilesUrl(listUrl)
                if (!articleSelf) return [collection]
                const bomRootFiles = await fetchBOMRootFiles(articleSelf)
                if (bomRootFiles && bomRootFiles.length > 0) {
                  filesWithArticleInfo = bomRootFiles
                }
              }

              const nextCollection: FileCollection = {
                ...collection,
                files: filesWithArticleInfo,
              }

              const articleSelf = deriveArticleSelfFromFilesUrl(listUrl)
              if (articleSelf) {
                const leafCollections = await fetchLeafArticleFileCollections(
                  articleSelf,
                  nextCollection.files,
                )
                if (leafCollections && leafCollections.length > 0) {
                  return [nextCollection, ...leafCollections]
                }
              }

              return [nextCollection]
            })
          )

          if (!isCancelled) {
            const nextCollections = refreshedCollections.flatMap((result, index) =>
              result.status === 'fulfilled' ? result.value : [collections[index]]
            )
            setFileCollections(nextCollections)
          }
        } else {
          // Handle string URLs, arrays, and undefined
          let listUrl: string | undefined
          
          if (typeof currentFiles === 'string') {
            listUrl = currentFiles
          } else if (Array.isArray(currentFiles) && !isFileCollectionArray(currentFiles)) {
            // If we have files already, use them immediately
            if (currentFiles.length > 0 && fileList === null) {
              setFileList(currentFiles)
            }
            listUrl = deriveListUrlFromFile(currentFiles[0])
            // Only set loading if we don't have files yet
            if (currentFiles.length === 0) {
              setLoading(true)
            }
          } else if (currentFiles === undefined || currentFiles === null) {
            // If files is undefined, we can't fetch - return
            setLoading(false)
            return
          } else {
            setLoading(true)
          }
          
          if (listUrl) {
            const response = await fetchFileList(listUrl, { accessSecret })
            const articleSelf = deriveArticleSelfFromFilesUrl(listUrl)
            if (articleSelf) {
              const leafCollections = await fetchLeafArticleFileCollections(
                articleSelf,
                response.results,
              )
              if (leafCollections && !isCancelled) {
                const rootCollection: FileCollection = {
                  title: t('item:content.heading.articleFiles', 'Article files'),
                  files: response.results,
                  filesUrl: listUrl,
                  downloadOnly,
                }
                setFileCollections([rootCollection, ...leafCollections])
                setFileList(null)
                return
              }
            }

            if (!isCancelled) {
              setFileList(response.results)
              setFileCollections(null)
            }
          }
        }
      } catch (error) {
        log.error('Error refreshing files when opening drawer', error)
        !isCancelled && toast.error(t('common:feedback.error.fetchFailed') || '')
      } finally {
        !isCancelled && setLoading(false)
      }
    }

    void refreshFiles()

    return () => {
      isCancelled = true
    }
  }, [open, files, t, refreshKey, accessSecret, socketRefreshKey])

  useEffect(() => {
    if (!open) return
    if (websocketMessage?.type !== 'files_scs_ready') return
    setSocketRefreshKey(prev => prev + 1)
  }, [open, websocketMessage])

  // Monitor if InfoPanel is open within this FilesDrawer (not on the main page)
  useEffect(() => {
    if (!open) {
      setLocalInfoPanelOpen(false)
      return
    }

    const checkInfoPanel = () => {
      // Find the drawerRoot container where MultilevelDrawer portals are rendered
      const drawerRoot = document.getElementById('drawerRoot')
      if (!drawerRoot) {
        setLocalInfoPanelOpen(false)
        return
      }

      // Check if there's an InfoPanel (Drawer) open within the drawerRoot
      // InfoPanel uses MuiDrawer-paper class
      const allDrawers = drawerRoot.querySelectorAll('.MuiDrawer-paper')
      let foundOpenPanel = false

      allDrawers.forEach((drawer) => {
        const style = window.getComputedStyle(drawer)
        const transform = style.transform
        // Drawer is open if transform is 'none' or contains 'translateX(0'
        if (transform === 'none' || transform.includes('translateX(0')) {
          // Check if this drawer is within a drawer container that contains our FilesDrawer
          // by traversing up the DOM tree
          let element = drawer.parentElement
          while (element && element !== drawerRoot) {
            // Check if this element contains our FilesDrawer container
            const filesDrawerContent = element.querySelector('[data-files-drawer-container]')
            if (filesDrawerContent) {
              foundOpenPanel = true
              break
            }
            element = element.parentElement
          }
        }
      })

      setLocalInfoPanelOpen(foundOpenPanel)
    }

    // Check immediately with a small delay to allow DOM to render
    const initialTimeout = setTimeout(checkInfoPanel, 50)

    // Set up MutationObserver to watch for InfoPanel changes in drawerRoot
    const drawerRoot = document.getElementById('drawerRoot')
    const observer = drawerRoot
      ? new MutationObserver(checkInfoPanel)
      : null

    if (observer && drawerRoot) {
      observer.observe(drawerRoot, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class'],
      })
    }

    // Also check periodically as a fallback
    const interval = setInterval(checkInfoPanel, 200)

    return () => {
      clearTimeout(initialTimeout)
      if (observer) observer.disconnect()
      clearInterval(interval)
      setLocalInfoPanelOpen(false)
    }
  }, [open])
  /* ------------------------------- Effects end ------------------------------ */

  const fetchLeafArticleFileCollections = async (
    articleSelf: string,
    rootFiles?: FileInfo[],
  ): Promise<FileCollection[] | null> => {
    try {
      const headers = accessSecret ? { 'X-ACCESS-SECRET': accessSecret } : undefined
      const leafArticles = await fetchBOMLeafArticles(articleSelf, undefined, headers)

      if (!Array.isArray(leafArticles) || leafArticles.length === 0) {
        return null
      }

      const collections = await Promise.allSettled(
        leafArticles.map(async leaf => {
          const filesUrl = leaf.files ? ensureTrailingSlash(leaf.files) : undefined
          const shouldFallbackToRoot = Boolean(leaf.isBomBase && rootFiles && rootFiles.length > 0)
          if (!filesUrl) {
            return {
              title: buildLeafArticleTitle(leaf),
              files: shouldFallbackToRoot ? rootFiles! : [],
              filesUrl: shouldFallbackToRoot ? undefined : filesUrl,
              downloadOnly,
            } satisfies FileCollection
          }

          const response = await fetchFileList(filesUrl, { accessSecret })
          if (response.results.length === 0 && shouldFallbackToRoot) {
            return {
              title: buildLeafArticleTitle(leaf),
              files: rootFiles!,
              filesUrl,
              downloadOnly,
            } satisfies FileCollection
          }
          return {
            title: buildLeafArticleTitle(leaf),
            files: response.results,
            filesUrl,
            downloadOnly,
          } satisfies FileCollection
        })
      )

      return collections.map((result, index) => {
        if (result.status === 'fulfilled') {
          return result.value
        }
        const fallbackLeaf = leafArticles[index]
        return {
          title: buildLeafArticleTitle(fallbackLeaf),
          files: [],
          filesUrl: fallbackLeaf.files,
          downloadOnly,
        }
      })
    } catch (error) {
      log.info('Skipping BOM leaf fetch (not a BOM base or request failed)', error)
      return null
    }
  }

  /**
   * Fetch files from the BOM root article for a given article.
   * Used as a fallback when a BOM article has no files attached.
   */
  const fetchBOMRootFiles = async (
    articleSelf: string,
    headers?: Record<string, string>
  ): Promise<FileInfo[] | null> => {
    try {
      const bomRoot = await fetchBOMRoot(articleSelf, headers)
      if (!bomRoot || !bomRoot.files) {
        return null
      }
      const filesUrl = ensureTrailingSlash(bomRoot.files)
      const response = await fetchFileList(filesUrl)
      return response.results
    } catch (error) {
      log.info('Could not fetch BOM root files for:', articleSelf, error)
      return null
    }
  }

  /* ------------------------- Utility functions start ------------------------ */

  /*const createFile = (fileInput: any) => {
    const file = {
      ...fileInput,
      key: fileInput.self,
      createdAt: new Date(fileInput.created_at).toLocaleString(),
    }
    delete file.created_at;
    return file
  }*/


  // async function fetchFileList(fileListUrl: string) {
  //   log.info('Begin fetching file list for', fileListUrl)
  //   const [response, error] = await handleAPICallV1(
  //     HTTPMethod.GET,
  //     fileListUrl
  //   )

  //   if(!error && response) {
  //     log.info('Success fetching file list for', fileListUrl)
  //     const files = response.results.map((file: any) => createFileInfo(file))

  //     if(response.next) {
  //       fetchFileList(response.next)
  //     }

  //     setFileList(prev => {
  //       // add fetched files to list or start a new list
  //       if(Array.isArray(prev)) return [...prev, ...files]
  //       else return files
  //     })
  //   } else {
  //     log.info('Error fetching file list', error)
  //     toast.error(t('common:feedback.error.fetchFailed') || '')
  //   }
  // }

  // async function fetchSingleFile(fileUrl: string) {
  //   log.info('Begin fetching file for', fileUrl)
  //   const [response, error] = await handleAPICallV1(
  //     HTTPMethod.GET,
  //     fileUrl,
  //   )

  //   if(!error && response) {
  //     log.info('Success fetching file list for', fileUrl)
  //     const file = createFileInfo(response)

  //     setFileList(prev => {
  //       // add fetched file to list or start a new list
  //       if(Array.isArray(prev)) return [...prev, file]
  //       else return [file]
  //     })
  //   } else {
  //     log.info('Error fetching file', error)
  //     toast.error(t('common:feedback.error.fetchFailed') || '')
  //   }
  // }

  /**
   * Takes an argument that describes a file / list of files using hyperlinks
   * or a FileInfo object.
   * Returns a list of FileInfo objects.
   * @param files
   */
  /*async function parseFilesInput(files: FileInfo[] | string[] | string | undefined) {

  }*/
  // const fetchObjectFiles = async (objectSelf: string) => {
  //   log.debug('fetchObjectFiles', objectSelf)
  //   const [response, error] = await handleAPICallV1(HTTPMethod.GET, objectSelf)

  //   if(!error && response) {
  //     const files = response.results.map((file: any) => createFile(file))
  //     log.debug('files', files)
  //     if(response.next) {
  //       fetchObjectFiles(response.next)
  //     }
  //     setFileList(files)
  //   } else {
  //     toast.error(t('common:feedback.error.fetchFailed') || '')
  //   }
  // }

  /*const handleDownload = async (url: string) => {
    try {
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', extractFilename(url) || '')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      toast.success(t('common:feedback.success.downloadStarted') || '')
    } catch (error) {
      console.error(error)
      toast.error(t('common:feedback.error.downloadFailed') || '')
    }
  }*/

  // const handleDeleteFile = async () => {
  //   log.debug('handleDeleteFile', deleteFile)
  //   const [response, error] = await handleAPICallV1(
  //     HTTPMethod.DELETE,
  //     deleteFile?.self,
  //     undefined,
  //     undefined,
  //     "text"
  //   )
  //   if(!error) {
  //     toast.success(t('common:feedback.success.deleteFile') || '')
  //     setOpenDelete(false)
  //     setFileList(fileList!.filter(file => file.self !== deleteFile?.self))
  //   } else {
  //     toast.error(t('common:feedback.error.deleteFile') || '')
  //     setOpenDelete(false)
  //   }
  // }

  /* -------------------------- Utility functions end ------------------------- */

  /* ------------------------ Callback functions start ------------------------ */
  const handleClose = (preventOnClose = false) => {
    // reset drawer state
    setLoading(false)
    setFilesToDelete([])
    setDeleteFileName('')
    setOpen(false)
    !preventOnClose && onClose()
  }

  /**
   * Checks if a file can be deleted by the current user.
   * Staff users cannot delete:
   * - Article files from other organizations that are used in requests
   * - Guest Request files
   * Note: Request files can be deleted by Staff users.
   * Note: This is a frontend check for UI purposes. The backend will enforce the final permission.
   */
  const canDeleteFile = (file: FileInfo): boolean => {
    if (!user || !isPrivileged(user, 'STAFF')) {
      return true  // Non-Staff-User: Standard-Behavior
    }
    
    // Staff users cannot delete Guest Request files
    if ((file as any).guestRequest || (file as any).guest_request || 
        extractSelfUrl((file as any).guestRequest ?? (file as any).guest_request)) {
      return false  // Staff-User cannot delete Guest Request files
    }
    
    // Check if file belongs to an article
    // If file has an article property, it's an article file
    if (file.article) {
      // If article is expanded (object), try to check root_account
      if (typeof file.article !== 'string') {
        const article = file.article as Article & { root_account?: string | { self?: string } }
        
        // Try to get root_account information if available
        if ('root_account' in article && article.root_account) {
          const articleRootAccountUrl = typeof article.root_account === 'string' 
            ? article.root_account 
            : (article.root_account as { self?: string })?.self
          
          // If we have both URLs and they don't match, disable deletion
          if (articleRootAccountUrl && user.organization && articleRootAccountUrl !== user.organization) {
            return false  // Staff-User cannot delete foreign article files
          }
          // If URLs match, allow deletion
          if (articleRootAccountUrl && user.organization && articleRootAccountUrl === user.organization) {
            return true  // Staff-User can delete own article files
          }
        }
      }
      // If we can't verify the organization (article is string or root_account not available),
      // allow deletion and let the backend enforce the final permission check.
      // The backend will reject the deletion if the user doesn't have permission.
      return true
    }
    
    // File doesn't belong to an article, request, or guest request, allow deletion
    return true
  }

  const handleDeleteFiles = async (): Promise<void> => {
    if (filesToDelete.length === 0) return

    // Filter files that can be deleted
    const deletableFiles = filesToDelete.filter(
      file =>
        typeof file.self === 'string' &&
        file.self !== '' &&
        canDeleteFile(file)
    )

    if (deletableFiles.length === 0) {
      toast.error(t('common:feedback.error.deleteFile') || 'Cannot delete files')
      setOpenDeleteModal(false)
      setFilesToDelete([])
      return
    }

    try {
      const deletePromises = deletableFiles.map(file =>
        deleteFileMutation.mutateAsync(file.self!)
      )
      const deleteResults = await Promise.allSettled(deletePromises)

      if (deleteResults.every(result => result.status === 'rejected')) {
        toast.error(t('common:feedback.error.deleteFile') || 'Error deleting files')
      } else if (deleteResults.some(result => result.status === 'rejected')) {
        toast.warn(t('common:feedback.warn.savingFileChanges') || 'Some files could not be deleted')
      } else {
        toast.success(
          deletableFiles.length === 1
            ? t('common:feedback.success.deleteFile') || 'File deleted successfully'
            : t('common:feedback.success.filesDeleted') || 'Files deleted successfully'
        )
      }

      // Refresh files after deletion
      setLoading(true)
      try {
        if (Array.isArray(files) && isFileCollectionArray(files)) {
          const collections = files
          const refreshedCollections = await Promise.allSettled(
            collections.map(async collection => {
              const listUrl = resolveCollectionListUrl(collection)
              if (!listUrl) return collection

              const originalFiles = collection.files || []
              const articleInfoMap = new Map<string, Article | string>()
              originalFiles.forEach((file: FileInfo) => {
                if (file.self && file.article) {
                  articleInfoMap.set(file.self, file.article)
                }
              })

              const response = await fetchFileList(listUrl)
              const filesWithArticleInfo = response.results.map((file: FileInfo) => {
                if (file.self && articleInfoMap.has(file.self)) {
                  return {
                    ...file,
                    article: articleInfoMap.get(file.self),
                  } as FileInfo
                }
                return file
              })

              return {
                ...collection,
                files: filesWithArticleInfo,
              }
            })
          )

          const nextCollections = refreshedCollections.map((result, index) =>
            result.status === 'fulfilled' ? result.value : collections[index]
          )
          setFileCollections(nextCollections)
        } else {
          const listUrl =
            typeof files === 'string'
              ? files
              : deriveListUrlFromFile(
                  Array.isArray(files) && !isFileCollectionArray(files)
                    ? files[0]
                    : undefined
                )

          if (listUrl) {
            const articleSelf = deriveArticleSelfFromFilesUrl(listUrl)
            if (articleSelf) {
              const leafCollections = await fetchLeafArticleFileCollections(articleSelf)
              if (leafCollections) {
                setFileCollections(leafCollections)
                setFileList(null)
                return
              }
            }

            const response = await fetchFileList(listUrl, { accessSecret })
            setFileList(response.results)
            setFileCollections(null)
          }
        }
      } catch (error) {
        log.error('Error refreshing files after deletion', error)
      } finally {
        setLoading(false)
      }

      setOpenDeleteModal(false)
      setFilesToDelete([])
      setDeleteFileName('')
      onConfirm()
    } catch (error) {
      log.error('Error deleting files', error)
      toast.error(t('common:feedback.error.deleteFile') || 'Error deleting files')
      setOpenDeleteModal(false)
      setFilesToDelete([])
      setDeleteFileName('')
    }
  }


  /*const extractFilename = (url: string) => {
    const urlParts = url?.split('?')[0]
    const pathParts = urlParts?.split('/')
    return pathParts?.pop()
  }*/
  /* ------------------------- Callback functions end ------------------------- */

  /* ------------------------- Render constants start ------------------------- */
  /* -------------------------- Render constants end -------------------------- */

  /* ------------------------ Pre render actions start ------------------------ */
  if(fileList === null && fileCollections === null) {
    // initialize file render state - only for arrays, not string URLs
    if(files && typeof files !== "string" && Array.isArray(files)) {
      // Determine if files is FileInfo[] or FileCollection[]
      const isFileCollectionArray = files.length > 0 && typeof files[0] === 'object' && files[0] !== null && 'files' in files[0]
      
      if(isFileCollectionArray) {
        // files contains list of FileCollection objects
        setFileCollections(files as FileCollection[])
      } else if(files.length > 0 && Object.hasOwn(files[0], 'self')) {
        // files contains a list of FileInfo objects
        setFileList(files as FileInfo[])
      } else if(files.length === 0) {
        // Empty array - initialize as FileInfo[] by default
        setFileList([])
      }
    }
    // If files is a string URL, don't initialize state - let refreshFiles handle it
  }
  /* ------------------------- Pre render actions end ------------------------- */

  log.debug(
    'files ->', files,

  )

  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */
  return (
    <>
      <MultilevelDrawer
        open={open}
        setOpen={setOpen}
        onClose={handleClose}
        size={size}
        title={title ?? ''}
        confirmButtonProps={{
          text: '',
          onConfirm: () => void 0,
          disabled: false,
          hidden: true,
        }}
      >
        <Box
          data-files-drawer-container
          sx={{
            width: '100%',
            boxSizing: 'border-box',
            paddingRight: localInfoPanelOpen && !isMobile ? '350px' : '0px',
            transition: 'padding-right 0.3s ease',
          }}
        >
        {fileCollections !== null &&
          fileCollections
            .filter(collection => collection.files && Array.isArray(collection.files))
            .map((collection, index) => {
              const collectionFilesUrl = resolveCollectionListUrl(collection)
              const tableFilesUrl = collection.files.length > 0 ? undefined : collectionFilesUrl
              // Check if this collection contains article files and if there are multiple collections
              // (which indicates we're in a request context where both request files and article files are shown)
              const hasArticleFiles = collection.files.some((file: FileInfo) => !!file.article)
              const isArticleFilesInRequest = hasArticleFiles && fileCollections.length > 1
              return (
                <Box key={index} mb={3}>
                  <Typography variant='h6' mb={2}>{collection.title}</Typography>
                  <UploadedFilesTable
                    files={collection.files}
                    filesUrl={tableFilesUrl}
                    isArticleFilesInRequest={isArticleFilesInRequest}
                    downloadOnly={downloadOnly || collection.downloadOnly}
                    onDelete={(file: FileInfo) => {
                      setFilesToDelete([file])
                      setDeleteFileName(file.name || t('common:content.label.thisFile') || 'this file')
                      setOpenDeleteModal(true)
                      return Promise.resolve()
                    }}
                    onDeleteMultiple={(files: FileInfo[]) => {
                      setFilesToDelete(files)
                      setDeleteFileName(
                        files.length === 1
                          ? files[0].name || t('common:content.label.thisFile') || 'this file'
                          : `${files.length} ${t('common:content.label.files') || 'files'}`
                      )
                      setOpenDeleteModal(true)
                      return Promise.resolve()
                    }}
                  />
                </Box>
              )
            })
        }
        {fileList !== null && (
          <UploadedFilesTable
            files={fileList}
            filesUrl={fileListUrl}
            downloadOnly={downloadOnly}
            onDelete={(file: FileInfo) => {
              setFilesToDelete([file])
              setDeleteFileName(file.name || t('common:content.label.thisFile') || 'this file')
              setOpenDeleteModal(true)
                return Promise.resolve()
              }}
              onDeleteMultiple={(files: FileInfo[]) => {
                setFilesToDelete(files)
                setDeleteFileName(
                  files.length === 1
                    ? files[0].name || t('common:content.label.thisFile') || 'this file'
                    : `${files.length} ${t('common:content.label.files') || 'files'}`
                )
                setOpenDeleteModal(true)
                return Promise.resolve()
              }}
            />
          )}
          <Grid container spacing={2}
            justifyContent="center"
            alignItems="center"
          >
              {/*fileList.length > 0 && !loading && (
                <Grid item xs={12}>
                  <TableContainer>
                    <Table>
                      <TableHead className="table-header">
                        <TableRow>
                          <TableCell>{t('common:content.label.fileName')}</TableCell>
                          <TableCell>{t('common:content.label.size')}</TableCell>
                          <TableCell>{t('common:content.label.createdAt')}</TableCell>
                          <TableCell>{t('common:content.label.action')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {fileList?.map((file, index) => (
                          <TableRow key={index}>
                            <TableCell>{extractFilename(file.document)}</TableCell>
                            <TableCell>{formatBytes(file.size)}</TableCell>
                            <TableCell>{moment(file.stored_at).format('DD.MM.YYYY HH:mm')}</TableCell>
                            <TableCell align="left">
                              <Stack direction="row" spacing={1}>
                                <Tooltip title={t('common:content.label.download') || ''}>
                                  <IconButton onClick={() => handleDownload(file.document)}>
                                    <Download />
                                  </IconButton>
                                </Tooltip>
                                {isPrivileged(user, "STAFF") && (
                                  <Tooltip title={t('common:content.label.delete') || ''}>
                                    <IconButton onClick={() => {
                                      setOpenDelete(true)
                                      setDeleteFileName(extractFilename(file.document) || '')
                                      setDeleteFile(file)
                                    }}>
                                      <Delete />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {(file.document.includes(".stl") || file.document.includes(".STL")) && (
                                  <Tooltip title={t('common:content.label.showStl') || ''}>
                                    <IconButton
                                      onClick={() => {
                                        setSelectedFile(file)
                                        setOpenStlViewer(true)
                                      }}
                                    >
                                      <ViewInAr />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {file.name.match(/.pdf$/i) &&
                                  <Tooltip title={t('common:content.label.showPdf') || ''}>
                                    <IconButton
                                      onClick={() => {
                                        setSelectedFile(file)
                                        setOpenPdfViewer(true)
                                      }}
                                    >
                                      <FileOpenRounded />
                                    </IconButton>
                                  </Tooltip>
                                }
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid> */}
              {/* )} */}
              {loading && (
                <Grid item xs={12}>
                  <Grid container
                    justifyContent={'center'}
                    alignItems={'center'}
                  >
                    <Grid item xs={12}>
                      <CircularProgress
                        sx={{
                          margin: '0 auto',
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <p>{t('common:content.label.loadingFiles') || ''}</p>
                    </Grid>
                  </Grid>
                </Grid>
              )}
          </Grid>
        </Box>
      </MultilevelDrawer>
      <DeleteModal
        name={deleteFileName}
        open={openDeleteModal}
        setOpen={setOpenDeleteModal}
        onDelete={handleDeleteFiles}
      />
    </>
  )
}

export { FilesDrawer }
export default FilesDrawer
